import { Injectable, inject, signal, computed } from '@angular/core';
import { doc, getDoc, onSnapshot, setDoc, Unsubscribe } from 'firebase/firestore';

import { Organization } from '../models/organization.model';
import { OrganizationMember } from '../models/member.model';
import { Role, DEFAULT_MEMBER_PERMISSIONS } from '../models/role.model';
import { DataStoreService } from './data-store.service';
import { FirebaseService } from './firebase.service';
import { OrganizationService } from './organization.service';
import { OrganizationMemberService } from './organization-member.service';

@Injectable({
  providedIn: 'root',
})
export class OrgContextService {
  private readonly firebase = inject(FirebaseService);
  private readonly dataStore = inject(DataStoreService);
  private readonly organizationService = inject(OrganizationService);
  private readonly memberService = inject(OrganizationMemberService);

  readonly currentOrg = computed(() => this.dataStore.currentOrganization());
  readonly currentMember = computed(() => this.dataStore.currentMember());
  readonly currentRole = computed(() => this.dataStore.currentRole());

  readonly userOrganizations = signal<Organization[]>([]);
  readonly loading = signal(false);

  private currentOrgUnsub: Unsubscribe | null = null;
  private currentMemberUnsub: Unsubscribe | null = null;
  private currentRoleUnsub: Unsubscribe | null = null;
  private subscribedOrgId: string | null = null;
  private subscribedRoleId: string | null = null;

  // The org the user last selected, shared across all Crewdle apps via the user
  // doc (`users/{uid}.currentOrganizationId`). Captured in loadUserOrganizations.
  private remoteCurrentOrgId: string | null = null;

  private get storageKey(): string {
    const uid = this.firebase.auth.currentUser?.uid;
    return `accord-current-org-${uid}`;
  }

  async init() {
    await this.firebase.auth.authStateReady();
    const user = this.firebase.auth.currentUser;
    if (!user) {
      console.error(
        '[OrgContext] init: no currentUser after authStateReady — session was torn down between the auth guard and org load',
      );
      return;
    }
    console.log(`[OrgContext] init: loading orgs for ${user.uid} (${user.email})`);

    await this.loadUserOrganizations();

    // Restore the active org. The user-doc `currentOrganizationId` is the
    // cross-app source of truth (shared with the other Crewdle apps); fall back
    // to this app's localStorage, then the first org.
    const orgs = this.userOrganizations();
    console.log(
      `[OrgContext] init: loaded ${orgs.length} orgs (${orgs.map((o) => o.name).join(', ')})`,
    );
    const savedOrgId = localStorage.getItem(this.storageKey);
    const target = [this.remoteCurrentOrgId, savedOrgId].find(
      (id): id is string => !!id && orgs.some((o) => o.id === id),
    );

    if (target) {
      await this.switchOrg(target);
    } else if (orgs.length > 0) {
      await this.switchOrg(orgs[0].id);
    }
  }

  async loadUserOrganizations() {
    const user = this.firebase.auth.currentUser;
    if (!user) return;

    const userDoc = await getDoc(doc(this.firebase.firestore, 'users', user.uid));
    const userData = userDoc.data();
    const orgIds: string[] = userData?.['organizationIds'] || [];
    if (!userDoc.exists() || orgIds.length === 0) {
      console.error(
        `[OrgContext] user doc ${user.uid} (${user.email}): exists=${userDoc.exists()}, organizationIds=${JSON.stringify(orgIds)}`,
      );
    }
    // Cross-app shared selection (same field the other Crewdle apps use).
    this.remoteCurrentOrgId = (userData?.['currentOrganizationId'] as string) || null;

    // Load every org in parallel — this is on the cold bootstrap path. Orgs the
    // user can no longer access resolve to null and are dropped.
    const orgs = (
      await Promise.all(
        orgIds.map((orgId) =>
          this.organizationService.getOrganization(orgId).catch((err) => {
            console.error(`[OrgContext] failed to load organization ${orgId}:`, err);
            return null;
          }),
        ),
      )
    ).filter((o): o is Organization => o !== null);

    this.userOrganizations.set(orgs);
  }

  /**
   * User-initiated org switch: persists the choice and hard-reloads so every
   * service / signal / Firestore subscription rehydrates against the new org.
   * Mirrors the other Crewdle apps.
   */
  switchOrgAndReload(orgId: string): void {
    const userOrgs = this.userOrganizations();
    if (!userOrgs.some((o) => o.id === orgId)) {
      throw new Error('Unauthorized: user is not a member of this organization');
    }
    localStorage.setItem(this.storageKey, orgId);
    // Persist the shared selection BEFORE reloading — init() prefers the
    // user-doc currentOrganizationId, so without this the reload would restore
    // the previous (stale) shared org instead of the one just selected.
    const uid = this.firebase.auth.currentUser?.uid;
    const persisted = uid
      ? setDoc(
          doc(this.firebase.firestore, 'users', uid),
          { currentOrganizationId: orgId },
          { merge: true },
        ).catch(() => undefined)
      : Promise.resolve();
    persisted.finally(() => {
      window.location.href = '/';
    });
  }

  async switchOrg(orgId: string) {
    this.loading.set(true);
    try {
      const userOrgs = this.userOrganizations();
      const organization = userOrgs.find((o) => o.id === orgId);
      if (!organization) {
        console.error('[OrgContext] switchOrg: user does not have access to organization', orgId);
        throw new Error('Unauthorized: user is not a member of this organization');
      }

      let member: OrganizationMember | null = null;
      let role: Role | null = null;

      try {
        member = await this.memberService.getCurrentMember(orgId);
      } catch {
        // Member doc may not exist yet
      }

      if (member) {
        const roleId = member.roleId || member.role;
        try {
          role = roleId ? await this.memberService.getRole(orgId, roleId) : null;
        } catch {
          role = null;
        }
        if (!role) {
          role = this.buildFallbackRoleFromMember(member, organization);
        }
      }

      // Fallback: if still no role but user is org owner, grant admin
      if (!role && organization.ownerId === this.firebase.auth.currentUser?.uid) {
        role = this.buildAdminRole();
      }

      this.dataStore.updateState({
        currentOrganization: organization,
        currentMember: member,
        currentRole: role,
      });

      localStorage.setItem(this.storageKey, orgId);
      // Persist the selection on the user doc so every Crewdle app picks up the
      // same active org. Only write on an actual change — init() calls
      // switchOrg on every load.
      const uid = this.firebase.auth.currentUser?.uid;
      if (uid && this.remoteCurrentOrgId !== orgId) {
        setDoc(
          doc(this.firebase.firestore, 'users', uid),
          { currentOrganizationId: orgId },
          { merge: true },
        ).catch(() => undefined);
      }
      this.remoteCurrentOrgId = orgId;
      this.startCurrentOrgSync(orgId);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Keep the current organization doc in sync so remote mutations (e.g. a
   * rename from another app) reflect live in the UI.
   */
  private startCurrentOrgSync(orgId: string): void {
    if (this.subscribedOrgId === orgId) return;
    this.currentOrgUnsub?.();
    this.currentMemberUnsub?.();
    this.currentRoleUnsub?.();
    this.currentRoleUnsub = null;
    this.subscribedRoleId = null;
    this.subscribedOrgId = orgId;

    const orgRef = doc(this.firebase.firestore, 'organizations', orgId);
    this.currentOrgUnsub = onSnapshot(orgRef, (snap) => {
      if (!snap.exists()) return;
      const current = this.dataStore.getProperty('currentOrganization');
      if (!current || current.id !== orgId) return;
      const data = snap.data();
      const updated: Organization = {
        ...current,
        name: data['name'] ?? current.name,
        licenseAccepted: data['licenseAccepted'] ?? current.licenseAccepted,
      };
      this.dataStore.updateState({ currentOrganization: updated });
    });

    const uid = this.firebase.auth.currentUser?.uid;
    if (!uid) return;
    const memberRef = doc(this.firebase.firestore, 'organizations', orgId, 'members', uid);
    this.currentMemberUnsub = onSnapshot(memberRef, (snap) => {
      if (this.subscribedOrgId !== orgId) return;
      if (!snap.exists()) {
        this.dataStore.updateState({ currentMember: null });
        this.resubscribeRole(orgId, null);
        return;
      }
      const data = snap.data();
      const updatedMember: OrganizationMember = {
        userId: data['userId'],
        role: data['role'],
        roleId: data['roleId'],
        permissions: data['permissions'],
        joinedAt: data['joinedAt'],
      };
      this.dataStore.updateState({ currentMember: updatedMember });
      const roleId = updatedMember.roleId || updatedMember.role || null;
      this.resubscribeRole(orgId, roleId);
    });
  }

  private resubscribeRole(orgId: string, roleId: string | null): void {
    if (this.subscribedRoleId === roleId) return;
    this.currentRoleUnsub?.();
    this.currentRoleUnsub = null;
    this.subscribedRoleId = roleId;

    if (!roleId) {
      const member = this.dataStore.getProperty('currentMember');
      const organization = this.dataStore.getProperty('currentOrganization');
      const fallback = member && organization
        ? this.buildFallbackRoleFromMember(member, organization)
        : null;
      this.dataStore.updateState({ currentRole: fallback });
      return;
    }

    if (roleId === 'admin') {
      this.dataStore.updateState({ currentRole: this.buildAdminRole() });
      return;
    }

    const roleRef = doc(this.firebase.firestore, 'organizations', orgId, 'roles', roleId);
    this.currentRoleUnsub = onSnapshot(roleRef, (snap) => {
      if (this.subscribedOrgId !== orgId || this.subscribedRoleId !== roleId) return;
      if (!snap.exists()) {
        const member = this.dataStore.getProperty('currentMember');
        const organization = this.dataStore.getProperty('currentOrganization');
        const fallback = member && organization
          ? this.buildFallbackRoleFromMember(member, organization)
          : null;
        this.dataStore.updateState({ currentRole: fallback });
        return;
      }
      const data = snap.data();
      const role: Role = {
        id: snap.id,
        name: data['name'],
        description: data['description'] || '',
        permissions: data['permissions'] || [],
        isSystem: data['isSystem'] ?? true,
        createdAt: data['createdAt'],
        updatedAt: data['updatedAt'],
        createdBy: data['createdBy'] || '',
      };
      this.dataStore.updateState({ currentRole: role });
    });
  }

  stopCurrentOrgSync(): void {
    this.currentOrgUnsub?.();
    this.currentMemberUnsub?.();
    this.currentRoleUnsub?.();
    this.currentOrgUnsub = null;
    this.currentMemberUnsub = null;
    this.currentRoleUnsub = null;
    this.subscribedOrgId = null;
    this.subscribedRoleId = null;
  }

  hasPermission(permission: string) {
    return computed(() => {
      const role = this.currentRole();
      if (!role) {
        return this.isCurrentUserOwner();
      }
      return role.permissions.includes(permission) || role.permissions.includes('*');
    });
  }

  async checkPermission(permission: string): Promise<boolean> {
    const role = this.currentRole();
    if (!role) {
      return this.isCurrentUserOwner();
    }
    return role.permissions.includes(permission) || role.permissions.includes('*');
  }

  private isCurrentUserOwner(): boolean {
    const org = this.currentOrg();
    const uid = this.firebase.auth.currentUser?.uid;
    return !!org && !!uid && org.ownerId === uid;
  }

  private buildFallbackRoleFromMember(member: OrganizationMember, organization: Organization): Role {
    const roleId = member.roleId || member.role;
    const isOwner = organization.ownerId === this.firebase.auth.currentUser?.uid;
    const isAdmin = isOwner || roleId === 'admin' || member.role === 'admin';
    return {
      id: roleId || 'member',
      name: (roleId || 'member').charAt(0).toUpperCase() + (roleId || 'member').slice(1),
      description: '',
      permissions: isAdmin ? ['*'] : DEFAULT_MEMBER_PERMISSIONS,
      isSystem: true,
      createdAt: null,
      updatedAt: null,
      createdBy: '',
    };
  }

  private buildAdminRole(): Role {
    return {
      id: 'admin',
      name: 'Admin',
      description: '',
      permissions: ['*'],
      isSystem: true,
      createdAt: null,
      updatedAt: null,
      createdBy: '',
    };
  }
}
